import React, { useState, useRef, useEffect } from "react";
import { FiArrowUp } from "react-icons/fi";
import { PiCoinsBold } from "react-icons/pi";

import {
  useAppContext,
  STYLE_OPTIONS,
  MODEL_TIERS,
} from "../../contexts/AppContext.jsx";
import { useAuth } from "../../contexts/AuthContext.jsx";
import { useUserProfile } from "../../hooks/useApi";
import { GenerationsToggle, GenerationsPopup } from "./GenerationsPanel.jsx";
import { SelectionHintTooltip } from "./SelectionHintTooltip.jsx";

export function GenerateBar({ onGenerate }) {
  const {
    editor,
    selectedStyle,
    setSelectedStyle,
    modelTier,
    setModelTier,
  } = useAppContext();
  const [prompt, setPrompt] = useState("");
  const [generationsOpen, setGenerationsOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const generationsRef = useRef(null);
  const { user } = useAuth();
  const { data: userProfile } = useUserProfile();

  // Close generations panel on canvas click (via editor) or click outside (via DOM)
  useEffect(() => {
    if (!generationsOpen) return;

    // Canvas clicks — use editor event to avoid passive listener warning
    const handleEditorEvent = ({ name }) => {
      if (name === "pointer_down") setGenerationsOpen(false);
    };
    if (editor) editor.on("event", handleEditorEvent);

    // Non-canvas clicks (e.g. elsewhere on the generate bar)
    const handleClickOutside = (e) => {
      if (generationsRef.current && !generationsRef.current.contains(e.target)) {
        setGenerationsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      if (editor) editor.off("event", handleEditorEvent);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [generationsOpen, editor]);

  const credits = (userProfile?.credits_remaining || 0) + (userProfile?.topup_credits || 0);

  // Listen to selection changes so the button reactively enables/disables
  const [hasSelection, setHasSelection] = useState(false);
  useEffect(() => {
    if (!editor) return;
    const update = () => {
      setHasSelection(editor.getSelectedShapes().length > 0);
    };
    update();
    const unsub = editor.store.listen(update, { scope: "all" });
    return unsub;
  }, [editor]);

  const handleGenerate = () => {
    if (!editor) return;

    const selectedShapes = editor.getSelectedShapes();
    if (selectedShapes.length === 0) return;

    if (!user) {
      window.location.href = "/profile";
      return;
    }

    if (credits !== undefined && credits <= 0) {
      window.location.href = "/profile";
      return;
    }

    onGenerate(editor, prompt, selectedStyle, modelTier);
    setPrompt("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
    e.stopPropagation();
  };

  const isDisabled = !hasSelection;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        padding: "12px 20px 14px",
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: "#fff",
        flexShrink: 0,
      }}
    >
      {/* Model tier + Style presets side by side */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          maxWidth: "100%",
        }}
      >
        {/* Model tier toggle — fixed, doesn't scroll */}
        <div
          style={{
            display: "flex",
            gap: "3px",
            padding: "3px",
            backgroundColor: "#f5f5f5",
            borderRadius: "9999px",
            flexShrink: 0,
          }}
        >
          {MODEL_TIERS.map((tier) => (
            <button
              key={tier.key}
              onClick={() => setModelTier(tier.key)}
              className="generate-bar-tier-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 600,
                color: modelTier === tier.key ? "#fff" : "#888",
                backgroundColor:
                  modelTier === tier.key ? "#000" : "transparent",
                border: "none",
                borderRadius: "9999px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background-color 0.15s ease, color 0.15s ease",
              }}
            >
              {tier.label} · <PiCoinsBold size={11} style={{ opacity: 0.7 }} />{tier.credits}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div
          style={{
            width: "1px",
            height: "20px",
            backgroundColor: "#ddd",
            flexShrink: 0,
          }}
        />

        {/* Style presets — scrollable */}
        <div
          className="generate-bar-styles"
          style={{
            display: "flex",
            gap: "3px",
            padding: "3px",
            backgroundColor: "#f5f5f5",
            borderRadius: "9999px",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            minWidth: 0,
          }}
        >
          {STYLE_OPTIONS.map((style) => (
            <button
              key={style.key}
              onClick={() => setSelectedStyle(style.key)}
              className="generate-bar-style-btn"
              style={{
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 600,
                color: selectedStyle === style.key ? "#fff" : "#555",
                backgroundColor:
                  selectedStyle === style.key ? "#000" : "transparent",
                border: "none",
                borderRadius: "9999px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Prompt + Generate + Generations toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          maxWidth: "600px",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            padding: "4px 4px 4px 16px",
            backgroundColor: "#f5f5f5",
            borderRadius: "12px",
          }}
        >
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              !hasSelection
                ? "Select your drawing first..."
                : "Describe the style you want..."
            }
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: "14px",
              color: "#333",
              backgroundColor: "transparent",
              fontFamily: "inherit",
            }}
          />
          <div
            style={{ position: "relative", flexShrink: 0 }}
            onMouseEnter={() => setShowHint(true)}
            onMouseLeave={() => setShowHint(false)}
          >
            {showHint && isDisabled && <SelectionHintTooltip />}
            <button
              onClick={handleGenerate}
              disabled={isDisabled}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                color: "#fff",
                backgroundColor: isDisabled ? "#ccc" : "#000",
                border: "none",
                borderRadius: "10px",
                cursor: isDisabled ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}
            >
              <FiArrowUp size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Generations toggle + popup */}
        <div ref={generationsRef} style={{ position: "relative" }}>
          <GenerationsToggle isOpen={generationsOpen} setIsOpen={setGenerationsOpen} />
          <GenerationsPopup isOpen={generationsOpen} />
        </div>
      </div>
    </div>
  );
}

export default GenerateBar;
